import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatEUR, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import SignaturePad from "../../components/SignaturePad";
import AuthImg from "../../components/pilotage/AuthImg";
import {
  ArrowLeft, ChevronRight, ChevronLeft, CheckCircle2, Circle, Camera, Gauge, Fuel,
  BatteryCharging, AlertTriangle, PenLine, FileText, KeyRound, ClipboardCheck, User,
  Car, Calendar, Phone, Mail, IdCard, Loader2, X, ShieldCheck, Smartphone, Lock, Hash,
} from "lucide-react";

const FUEL_LEVELS = ["1/4", "1/2", "3/4", "Plein"];

const STEPS = [
  { key: "verify", label: "Vérification", icon: ClipboardCheck },
  { key: "photos", label: "Photos", icon: Camera },
  { key: "readings", label: "Relevés", icon: Gauge },
  { key: "damages", label: "Dommages", icon: AlertTriangle },
  { key: "sign_edl", label: "Signature EDL", icon: PenLine },
  { key: "contract", label: "Contrat", icon: FileText },
  { key: "keys", label: "Remise des clés", icon: KeyRound },
];

export default function DepartureFlow() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [damagesReviewed, setDamagesReviewed] = useState(false);
  const [contract, setContract] = useState(null);

  const load = () =>
    api.get(`/vendor/bookings/${bookingId}/departure-state`)
      .then(({ data }) => { setState(data); return data; })
      .catch(() => setState(null))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, [bookingId]); // eslint-disable-line

  const loadContract = () =>
    api.get(`/contracts/${bookingId}`).then(({ data }) => setContract(data.contract)).catch(() => {});
  useEffect(() => { loadContract(); }, [bookingId]); // eslint-disable-line

  const insp = state?.inspection;
  const setInsp = (updated) => setState((s) => ({ ...s, inspection: updated }));

  const ensureInspection = async () => {
    if (state?.inspection) return state.inspection;
    try {
      const { data } = await api.post(`/inspections?booking_id=${bookingId}&type=departure`);
      setState((s) => ({ ...s, inspection: data.inspection, edl: { ...s.edl, created: true, id: data.inspection.id } }));
      return data.inspection;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossible de créer l'état des lieux");
      return null;
    }
  };

  const goTo = async (i) => {
    // Sauvegarde silencieuse avant de quitter les étapes éditables (navigation libre sans perte)
    const currentKey = STEPS[step]?.key;
    if (insp && !edlLocked && i !== step) {
      if (currentKey === "readings") await saveReadings(insp);
      if (currentKey === "damages") await saveDamages(insp);
    }
    if (i >= 1 && i <= 4 && !state?.inspection) await ensureInspection();
    setStep(i);
    window.scrollTo({ top: 0 });
  };

  const done = useMemo(() => {
    if (!state) return {};
    const c = state.checklist || {};
    const readingsDone = insp?.mileage != null && (!!insp?.fuel_level || insp?.battery_level != null);
    return {
      verify: c.info_verified,
      photos: c.photos_done,
      readings: readingsDone,
      damages: damagesReviewed || c.edl_signed,
      sign_edl: c.edl_signed,
      contract: c.contract_signed,
      keys: c.keys_handed,
    };
  }, [state, insp, damagesReviewed]);

  const doneCount = Object.values(done).filter(Boolean).length;
  const progressPct = Math.round((doneCount / STEPS.length) * 100);
  const edlLocked = !!(insp?.client_signature || insp?.vendor_signature);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  }
  if (!state) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 text-center" data-testid="departure-not-found">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Réservation introuvable</h2>
        <button onClick={() => navigate("/vendor/pilotage?tab=today")} className="mt-4 rounded-full bg-slate-900 text-white text-sm font-semibold px-5 py-2.5">Retour aux opérations du jour</button>
      </div>
    );
  }

  const { booking, vehicle, checklist } = state;
  const stepKey = STEPS[step].key;

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="departure-flow">
      {/* ─── Header sticky ─── */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 pt-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/vendor/pilotage?tab=today")} className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 shrink-0" data-testid="departure-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-violet-600">Départ · {booking.reference || booking.id.slice(0, 8).toUpperCase()}</div>
              <div className="text-sm font-semibold text-slate-900 truncate">{booking.client_name} — {booking.vehicle_name}</div>
            </div>
            <div className="text-xs font-bold text-slate-500 shrink-0" data-testid="departure-progress-pct">{progressPct}%</div>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-2.5 overflow-hidden">
            <div className="h-full bg-violet-600 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
          {/* Stepper — navigation libre */}
          <div className="flex gap-1.5 overflow-x-auto py-2.5 -mx-1 px-1 scrollbar-none">
            {STEPS.map((s, i) => {
              const isDone = done[s.key];
              const active = i === step;
              return (
                <button
                  key={s.key}
                  onClick={() => goTo(i)}
                  className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-semibold whitespace-nowrap shrink-0 border transition-colors ${
                    active ? "bg-slate-900 text-white border-slate-900"
                      : isDone ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`departure-step-${s.key}`}
                >
                  {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />}
                  {i + 1}. {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Contenu de l'étape ─── */}
      <div className="max-w-2xl mx-auto px-4 py-5">
        {stepKey === "verify" && <StepVerify state={state} onVerified={load} />}
        {stepKey === "photos" && insp && <StepPhotos insp={insp} setInsp={setInsp} locked={edlLocked} onChanged={load} />}
        {stepKey === "readings" && insp && <StepReadings insp={insp} setInsp={setInsp} vehicle={vehicle} locked={edlLocked} />}
        {stepKey === "damages" && insp && <StepDamages insp={insp} setInsp={setInsp} locked={edlLocked} onReviewed={() => setDamagesReviewed(true)} />}
        {(stepKey === "photos" || stepKey === "readings" || stepKey === "damages") && !insp && (
          <CreateEdlPrompt onCreate={ensureInspection} />
        )}
        {stepKey === "sign_edl" && <StepSignEdl state={state} insp={insp} onCreate={ensureInspection} onSigned={load} />}
        {stepKey === "contract" && <StepContract bookingId={bookingId} booking={booking} contract={contract} onSigned={() => { loadContract(); load(); }} />}
        {stepKey === "keys" && <StepKeys bookingId={bookingId} state={state} onDone={load} goTo={(k) => goTo(STEPS.findIndex((s) => s.key === k))} />}
      </div>

      {/* ─── Barre de navigation basse ─── */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <button
            onClick={() => goTo(Math.max(0, step - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-30"
            data-testid="departure-prev"
          >
            <ChevronLeft className="h-4 w-4" /> Précédent
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={async () => {
                if (stepKey === "readings" && insp && !edlLocked) await saveReadings(insp);
                if (stepKey === "damages" && insp && !edlLocked) { await saveDamages(insp); setDamagesReviewed(true); }
                goTo(step + 1);
              }}
              className="inline-flex items-center gap-1.5 h-11 px-6 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700"
              data-testid="departure-next"
            >
              Continuer <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <div className="text-xs font-semibold text-slate-500">
              {checklist.keys_handed ? "Départ terminé ✓" : "Dernière étape"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Helpers sauvegarde ───
export async function saveReadings(insp) {
  try {
    await api.put(`/inspections/${insp.id}`, {
      step: "mileage", mileage: insp.mileage, fuel_level: insp.fuel_level, battery_level: insp.battery_level ?? null,
    });
  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur d'enregistrement"); }
}
export async function saveDamages(insp) {
  try {
    await api.put(`/inspections/${insp.id}`, { step: "damages", zones: insp.zones, general_comment: insp.general_comment });
  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur d'enregistrement"); }
}

export function CreateEdlPrompt({ onCreate }) {
  const [creating, setCreating] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center" data-testid="create-edl-prompt">
      <ClipboardCheck className="h-8 w-8 text-violet-600 mx-auto mb-3" />
      <div className="font-semibold text-slate-900 mb-1">État des lieux de départ</div>
      <p className="text-sm text-slate-500 mb-4">Créez l'état des lieux pour commencer les photos et les relevés.</p>
      <button
        onClick={async () => { setCreating(true); await onCreate(); setCreating(false); }}
        disabled={creating}
        className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-violet-600 text-white text-sm font-bold disabled:opacity-50"
        data-testid="create-edl-btn"
      >
        {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />} Créer l'état des lieux
      </button>
    </div>
  );
}

export function StepCard({ title, instruction, children, testid }) {
  return (
    <div data-testid={testid}>
      <h2 className="font-heading text-xl font-semibold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500 mt-1 mb-4">{instruction}</p>
      {children}
    </div>
  );
}

// ─── Étape 1 : Vérification ───
function StepVerify({ state, onVerified }) {
  const { booking, vehicle, client, location_name, checklist } = state;
  const [verifying, setVerifying] = useState(false);
  const docs = client?.documents || [];
  const hasLicenseDoc = docs.some((d) => d.type === "driver_license");
  const paid = booking.amount_paid || 0;
  const balance = Math.round(((booking.total || 0) - paid) * 100) / 100;

  const verify = async () => {
    setVerifying(true);
    try {
      await api.post(`/vendor/bookings/${booking.id}/departure/verify`, {});
      toast.success("Informations vérifiées");
      onVerified();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally { setVerifying(false); }
  };

  return (
    <StepCard title="Vérification du départ" instruction="Vérifiez le client, le véhicule et la réservation avant de commencer." testid="step-verify">
      <div className="space-y-3">
        {/* Client */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="verify-client-card">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Client & conducteurs</div>
          <InfoRow icon={User} label="Conducteur principal" value={booking.client_name} />
          <InfoRow icon={Phone} label="Téléphone" value={booking.client_phone || "—"} />
          <InfoRow icon={Mail} label="Email" value={booking.client_email} />
          <InfoRow icon={IdCard} label="N° de permis" value={client?.license_number || "Non renseigné"} warn={!client?.license_number} />
          {(booking.drivers || []).map((d, i) => (
            <InfoRow key={i} icon={User} label={`Conducteur supplémentaire ${i + 1}`} value={`${d.name || "—"}${d.license_number ? ` · ${d.license_number}` : ""}`} />
          ))}
          <div className={`mt-2 flex items-center gap-1.5 text-xs font-semibold ${hasLicenseDoc ? "text-emerald-600" : "text-amber-600"}`} data-testid="verify-license-doc">
            {hasLicenseDoc ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {hasLicenseDoc ? "Permis de conduire au dossier" : "Aucune copie du permis au dossier — pensez à la scanner depuis la fiche réservation"}
          </div>
        </div>

        {/* Véhicule */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="verify-vehicle-card">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Véhicule</div>
          <InfoRow icon={Car} label="Véhicule" value={booking.vehicle_name} />
          <InfoRow icon={Hash} label="Immatriculation" value={vehicle.license_plate || "—"} mono />
          <InfoRow icon={Gauge} label="Kilométrage connu" value={vehicle.mileage != null ? `${vehicle.mileage.toLocaleString("fr-FR")} km` : "—"} />
          {location_name && <InfoRow icon={Car} label="Agence" value={location_name} />}
        </div>

        {/* Réservation */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="verify-booking-card">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Réservation</div>
          <InfoRow icon={Calendar} label="Période" value={`${formatDateFR(booking.start_date)} ${booking.start_time || ""} → ${formatDateFR(booking.end_date)} ${booking.end_time || ""}`} />
          <InfoRow icon={Calendar} label="Durée" value={`${booking.days} jour${booking.days > 1 ? "s" : ""}`} />
          <InfoRow icon={FileText} label="Total" value={formatEUR(booking.total || 0)} />
          {(booking.options || []).length > 0 && (
            <InfoRow icon={FileText} label="Options" value={(booking.options || []).map((o) => o.label).join(", ")} />
          )}
          <InfoRow icon={FileText} label="Payé / Solde" value={`${formatEUR(paid)} payé · ${balance > 0 ? `${formatEUR(balance)} restant` : "soldé"}`} warn={balance > 0} />
          <InfoRow icon={ShieldCheck} label="Caution" value={booking.caution_amount ? `${formatEUR(booking.caution_amount)} (${cautionLabel(booking.caution_status)})` : "Non demandée"} />
        </div>

        {/* Statuts documents */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="verify-status-card">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Avancement</div>
          <StatusRow ok={state.contract.signed} label="Contrat" okText={`Signé${state.contract.reference ? ` · ${state.contract.reference}` : ""}`} koText="À faire signer (étape 6)" />
          <StatusRow ok={state.edl.signed} label="État des lieux départ" okText="Signé" koText={state.edl.created ? "En cours" : "À réaliser (étapes 2 à 5)"} />
        </div>
      </div>

      {checklist.info_verified ? (
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-semibold text-emerald-700" data-testid="verify-done-banner">
          <CheckCircle2 className="h-4 w-4" /> Informations vérifiées {booking.departure_verified_by ? `par ${booking.departure_verified_by}` : ""}
        </div>
      ) : (
        <button
          onClick={verify}
          disabled={verifying}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-50"
          data-testid="verify-confirm-btn"
        >
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Informations vérifiées
        </button>
      )}
    </StepCard>
  );
}

export function cautionLabel(s) {
  return { none: "non demandée", pending: "en attente", held: "immobilisée", captured: "retenue", released: "libérée" }[s || "none"] || s;
}

export function InfoRow({ icon: Icon, label, value, warn, mono }) {
  return (
    <div className="flex items-start gap-2.5 py-1.5 text-sm">
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${warn ? "text-amber-500" : "text-slate-400"}`} />
      <div className="min-w-0">
        <span className="text-slate-500">{label} : </span>
        <span className={`font-medium ${warn ? "text-amber-700" : "text-slate-900"} ${mono ? "font-mono" : ""}`}>{value}</span>
      </div>
    </div>
  );
}

export function StatusRow({ ok, label, okText, koText }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`inline-flex items-center gap-1.5 font-semibold text-xs ${ok ? "text-emerald-600" : "text-amber-600"}`}>
        {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />} {ok ? okText : koText}
      </span>
    </div>
  );
}

// ─── Étape 2 : Photos ───
export function StepPhotos({ insp, setInsp, locked, onChanged }) {
  const fileRef = useRef(null);
  const [activeZone, setActiveZone] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeZone) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/inspections/${insp.id}/photo?zone=${encodeURIComponent(activeZone)}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const zones = insp.zones.map((z) => (z.name === activeZone ? { ...z, photos: [...z.photos, data.path] } : z));
      setInsp({ ...insp, zones });
      onChanged();
      toast.success(`Photo ajoutée — ${activeZone}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'upload — réessayez");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (zoneName, path) => {
    try {
      await api.delete(`/inspections/${insp.id}/photo?zone=${encodeURIComponent(zoneName)}&path=${encodeURIComponent(path)}`);
      const zones = insp.zones.map((z) => (z.name === zoneName ? { ...z, photos: z.photos.filter((p) => p !== path) } : z));
      setInsp({ ...insp, zones });
      onChanged();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const totalPhotos = (insp.zones || []).reduce((s, z) => s + z.photos.length, 0);
  const zonesDone = (insp.zones || []).filter((z) => z.photos.length > 0).length;

  return (
    <StepCard title="Photos du véhicule" instruction="Photographiez chaque zone. Au moins 1 photo par zone est requise avant la signature." testid="step-flow-photos">
      <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-3">
        <span data-testid="photos-zones-count">{zonesDone}/{(insp.zones || []).length} zones couvertes</span>
        <span>{totalPhotos} photo{totalPhotos !== 1 ? "s" : ""}</span>
      </div>
      <div className="space-y-3">
        {(insp.zones || []).map((z) => (
          <div key={z.name} className="rounded-2xl border border-slate-200 bg-white p-3.5" data-testid={`photo-zone-${z.name}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {z.photos.length > 0 ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-slate-300" />}
                <span className="text-sm font-semibold text-slate-900">{z.name}</span>
                <span className="text-[10px] font-bold text-slate-400">{z.photos.length} photo{z.photos.length !== 1 ? "s" : ""}</span>
              </div>
              {!locked && (
                <button
                  onClick={() => { setActiveZone(z.name); fileRef.current?.click(); }}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-violet-600 text-white text-[11px] font-bold hover:bg-violet-700 disabled:opacity-50"
                  data-testid={`photo-add-${z.name}`}
                >
                  <Camera className="h-3.5 w-3.5" /> {z.photos.length > 0 ? "Ajouter" : "Photographier"}
                </button>
              )}
            </div>
            {z.photos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {z.photos.map((p, idx) => (
                  <div key={p} className="relative">
                    <AuthImg
                      path={p}
                      className="h-16 w-16 rounded-lg object-cover border border-slate-200 cursor-pointer"
                      onClick={() => setLightbox(p)}
                      data-testid={`photo-thumb-${z.name}-${idx}`}
                    />
                    {!locked && (
                      <button
                        onClick={() => handleDelete(z.name, p)}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-red-600"
                        data-testid={`photo-remove-${z.name}-${idx}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUpload} />
      {uploading && (
        <div className="mt-3 flex items-center gap-2 text-sm text-violet-600 font-semibold" data-testid="photo-uploading">
          <Loader2 className="h-4 w-4 animate-spin" /> Envoi de la photo…
        </div>
      )}
      {locked && <LockedBanner />}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <AuthImg path={lightbox} className="max-h-[85vh] max-w-full rounded-xl object-contain" />
          <button className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center" onClick={() => setLightbox(null)}>
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </StepCard>
  );
}

export function LockedBanner() {
  return (
    <div className="mt-4 flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-3 text-xs font-semibold text-slate-600" data-testid="edl-locked-banner">
      <Lock className="h-3.5 w-3.5" /> État des lieux signé — les éléments sont verrouillés.
    </div>
  );
}

// ─── Étape 3 : Relevés (km + énergie) ───
export function StepReadings({ insp, setInsp, vehicle, locked, title = "Kilométrage & énergie", instruction = "Relevez le compteur du véhicule au moment du départ.", minMileage = null, children }) {
  const energy = (insp.vehicle_energy || vehicle.fuel || "").toLowerCase();
  const isElectric = energy.includes("électrique") || energy.includes("electrique");
  const isHybrid = energy.includes("hybride");
  const showFuel = !isElectric || isHybrid;
  const showBattery = isElectric || isHybrid;
  const knownMileage = minMileage != null ? minMileage : vehicle.mileage;
  const incoherent = insp.mileage != null && knownMileage != null && insp.mileage < knownMileage;

  return (
    <StepCard title={title} instruction={instruction} testid="step-flow-readings">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-5">
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
            <Gauge className="h-4 w-4 text-violet-600" /> Kilométrage au départ <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            inputMode="numeric"
            value={insp.mileage ?? ""}
            onChange={(e) => setInsp({ ...insp, mileage: e.target.value === "" ? null : parseInt(e.target.value) || 0 })}
            disabled={locked}
            className="w-full rounded-xl border border-slate-200 px-4 py-3.5 text-lg font-semibold focus:ring-2 focus:ring-violet-600 outline-none"
            placeholder={knownMileage != null ? `Dernier relevé : ${knownMileage.toLocaleString("fr-FR")} km` : "Ex : 45 230"}
            data-testid="flow-mileage-input"
          />
          {incoherent && (
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-medium text-amber-700" data-testid="mileage-warning">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Le kilométrage saisi ({insp.mileage.toLocaleString("fr-FR")} km) est inférieur au dernier relevé connu ({knownMileage.toLocaleString("fr-FR")} km). Vérifiez le compteur.
            </div>
          )}
        </div>
        {showFuel && (
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
              <Fuel className="h-4 w-4 text-violet-600" /> Niveau de carburant <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-4 gap-2">
              {FUEL_LEVELS.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => !locked && setInsp({ ...insp, fuel_level: l })}
                  disabled={locked}
                  className={`py-3 rounded-xl border-2 text-sm font-bold transition-all ${
                    insp.fuel_level === l ? "border-violet-600 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600"
                  }`}
                  data-testid={`flow-fuel-${l}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}
        {showBattery && (
          <div data-testid="flow-battery-block">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
              <BatteryCharging className="h-4 w-4 text-emerald-600" /> Niveau de batterie ({insp.battery_level ?? "—"} %)
            </label>
            <input
              type="range" min="0" max="100" step="5"
              value={insp.battery_level ?? 50}
              onChange={(e) => setInsp({ ...insp, battery_level: parseInt(e.target.value) })}
              disabled={locked}
              className="w-full accent-emerald-600"
              data-testid="flow-battery-slider"
            />
          </div>
        )}
        {children}
      </div>
      {locked && <LockedBanner />}
    </StepCard>
  );
}

// ─── Étape 4 : Dommages ───
export function StepDamages({ insp, setInsp, locked, onReviewed, departureZones = null, title = "Dommages & observations", instruction = "Signalez les dommages existants zone par zone. Ils protègent le client comme le loueur." }) {
  const updateZone = (idx, field, value) => {
    const zones = [...insp.zones];
    zones[idx] = { ...zones[idx], [field]: value };
    setInsp({ ...insp, zones });
  };
  const damaged = (insp.zones || []).filter((z) => z.damages);
  const depMap = departureZones ? Object.fromEntries(departureZones.map((z) => [z.name, z])) : null;

  return (
    <StepCard title={title} instruction={instruction} testid="step-flow-damages">
      <div className="space-y-3">
        {(insp.zones || []).map((z, i) => (
          <div key={z.name} className={`rounded-2xl border bg-white p-4 ${z.damages ? "border-amber-300" : "border-slate-200"}`} data-testid={`damage-zone-${z.name}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-slate-900">{z.name}</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-xs text-slate-500">Dommage</span>
                <input
                  type="checkbox"
                  checked={z.damages}
                  onChange={(e) => updateZone(i, "damages", e.target.checked)}
                  disabled={locked}
                  className="h-4 w-4 rounded border-slate-300 text-violet-600"
                  data-testid={`damage-toggle-${z.name}`}
                />
              </label>
            </div>
            {depMap && depMap[z.name] && (
              <div className="mb-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-100 text-[10px] font-bold text-slate-600" data-testid={`departure-damage-badge-${z.name}`}>
                Présent au départ : {{ minor: "Léger", medium: "Moyen", major: "Important" }[depMap[z.name].severity] || "signalé"}{depMap[z.name].comment ? ` — ${depMap[z.name].comment}` : ""}
              </div>
            )}
            {z.damages && (
              <div className="flex items-center gap-1.5 my-2">
                {[["minor", "Léger", "bg-amber-100 text-amber-700 border-amber-300"], ["medium", "Moyen", "bg-orange-100 text-orange-700 border-orange-300"], ["major", "Important", "bg-red-100 text-red-700 border-red-300"]].map(([k, l, cls]) => (
                  <button
                    key={k}
                    type="button"
                    disabled={locked}
                    onClick={() => updateZone(i, "severity", z.severity === k ? null : k)}
                    className={`px-3 py-1.5 rounded-lg border text-[11px] font-bold ${z.severity === k ? cls : "border-slate-200 text-slate-500"}`}
                    data-testid={`damage-severity-${z.name}-${k}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
            <input
              value={z.comment}
              onChange={(e) => updateZone(i, "comment", e.target.value)}
              disabled={locked}
              placeholder="Commentaire (optionnel)"
              className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none"
            />
          </div>
        ))}
      </div>
      <div className="mt-3">
        <label className="text-sm font-semibold text-slate-900 mb-1.5 block">Commentaire général</label>
        <textarea
          value={insp.general_comment || ""}
          onChange={(e) => setInsp({ ...insp, general_comment: e.target.value })}
          disabled={locked}
          rows={3}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-violet-600 outline-none resize-none"
          placeholder="Remarques générales…"
          data-testid="flow-general-comment"
        />
      </div>
      <div className={`mt-3 rounded-xl px-4 py-3 text-sm font-semibold ${damaged.length ? "bg-amber-50 border border-amber-200 text-amber-700" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`} data-testid="damages-recap">
        {damaged.length
          ? `${damaged.length} zone${damaged.length > 1 ? "s" : ""} avec dommage : ${damaged.map((z) => z.name).join(", ")}`
          : "Aucun dommage signalé"}
      </div>
      {locked && <LockedBanner />}
    </StepCard>
  );
}

// ─── Bloc signature présentielle (partagé EDL + contrat) ───
export function PresentialSignBlock({ defaultName, mentionDoc, onSign, signing, extraChildren, allowReserve = false, blockedReason = null }) {
  const [signerName, setSignerName] = useState(defaultName || "");
  const [consent, setConsent] = useState(false);
  const [reserve, setReserve] = useState("");
  const canSign = consent && signerName.trim().length > 1;

  return (
    <div className="rounded-2xl border-2 border-violet-200 bg-violet-50/50 p-4 mt-4" data-testid="presential-sign-block">
      <div className="flex items-center gap-2 mb-3">
        <Smartphone className="h-4 w-4 text-violet-600" />
        <div className="text-sm font-bold text-violet-900">Remettez le téléphone au client</div>
      </div>
      <p className="text-xs text-slate-600 mb-4">
        Le client consulte le document ci-dessus puis signe directement sur cet appareil. La signature est horodatée
        avec la mention « Signé en présence du loueur sur son appareil ».
      </p>
      {extraChildren}
      <label className="text-xs font-semibold text-slate-700 block mb-1.5">Nom et prénom du signataire <span className="text-red-500">*</span></label>
      <input
        value={signerName}
        onChange={(e) => setSignerName(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-violet-600 outline-none mb-3"
        placeholder="Prénom Nom"
        data-testid="presential-signer-name"
      />
      {allowReserve && (
        <div className="mb-3">
          <label className="text-xs font-semibold text-slate-700 block mb-1.5">Réserve ou contestation du client (optionnel)</label>
          <textarea
            value={reserve}
            onChange={(e) => setReserve(e.target.value)}
            rows={2}
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:ring-2 focus:ring-violet-600 outline-none"
            placeholder="Ex : je conteste le dommage sur l'aile avant droite…"
            data-testid="presential-reserve-input"
          />
          <p className="text-[10px] text-slate-500 mt-1">La signature avec réserve ne vaut pas acceptation sans condition des montants. La réserve est jointe au document.</p>
        </div>
      )}
      <label className="flex items-start gap-2.5 cursor-pointer mb-4" data-testid="presential-consent-label">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="h-4 w-4 mt-0.5 rounded border-slate-300 text-violet-600"
          data-testid="presential-consent-checkbox"
        />
        <span className="text-xs text-slate-600">{mentionDoc}</span>
      </label>
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-amber-700">
        <Lock className="h-3 w-3" /> Après signature, le document sera définitivement verrouillé.
      </div>
      {blockedReason && (
        <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-xs font-bold text-red-700" data-testid="sign-blocked-hint">
          {blockedReason}
        </div>
      )}
      <div className={canSign && !blockedReason ? "" : "opacity-40 pointer-events-none"}>
        <SignaturePad onConfirm={(sig) => onSign({ signature_data: sig, signer_name: signerName.trim(), consent, reserve_text: allowReserve ? reserve.trim() : undefined })} disabled={signing || !canSign} />
      </div>
    </div>
  );
}

// ─── Étape 5 : Signature EDL ───
function StepSignEdl({ state, insp, onCreate, onSigned }) {
  const [signing, setSigning] = useState(false);
  const [exceptionReason, setExceptionReason] = useState("");
  const [showException, setShowException] = useState(false);

  if (!insp) return <CreateEdlPrompt onCreate={onCreate} />;

  const signed = !!insp.client_signature;
  const zonesMissing = (insp.zones || []).filter((z) => !(z.photos || []).length).map((z) => z.name);
  const damaged = (insp.zones || []).filter((z) => z.damages);
  const totalPhotos = (insp.zones || []).reduce((s, z) => s + z.photos.length, 0);
  const SEV = { minor: "Léger", medium: "Moyen", major: "Important" };

  const sign = async ({ signature_data, signer_name, consent }) => {
    setSigning(true);
    try {
      await api.put(`/inspections/${insp.id}`, {
        step: "sign_client_presential",
        signature_data, signer_name, consent,
        photos_exception_reason: exceptionReason.trim() || null,
      });
      toast.success("État des lieux signé et verrouillé");
      onSigned();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur lors de la signature");
    } finally { setSigning(false); }
  };

  return (
    <StepCard title="Signature de l'état des lieux" instruction="Faites relire le récapitulatif au client, puis recueillez sa signature." testid="step-flow-sign-edl">
      {/* Récapitulatif complet pour le client */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="edl-recap">
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-3">
          Récapitulatif — {insp.reference || "État des lieux de départ"}
        </div>
        <div className="grid grid-cols-2 gap-2.5 text-sm mb-4">
          <RecapTile label="Véhicule" value={state.booking.vehicle_name} />
          <RecapTile label="Kilométrage" value={insp.mileage != null ? `${insp.mileage.toLocaleString("fr-FR")} km` : "—"} warn={insp.mileage == null} />
          {insp.battery_level != null
            ? <RecapTile label="Batterie" value={`${insp.battery_level} %${insp.fuel_level ? ` · ${insp.fuel_level}` : ""}`} />
            : <RecapTile label="Carburant" value={insp.fuel_level || "—"} warn={!insp.fuel_level} />}
          <RecapTile label="Photos" value={`${totalPhotos}`} />
          <RecapTile label="Date & heure" value={new Date().toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} />
        </div>
        <div className="text-xs font-bold text-slate-500 mb-1.5">Dommages constatés</div>
        {damaged.length ? (
          <div className="space-y-1 mb-3">
            {damaged.map((z) => (
              <div key={z.name} className="flex items-center justify-between text-xs bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                <span className="font-semibold text-slate-800">{z.name}{z.comment ? ` — ${z.comment}` : ""}</span>
                <span className="font-bold text-amber-700">{SEV[z.severity] || "—"}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-3">Aucun dommage signalé</div>
        )}
        {insp.general_comment && <div className="text-xs text-slate-600 italic mb-2">« {insp.general_comment} »</div>}
        {totalPhotos > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100">
            {(insp.zones || []).flatMap((z) => z.photos.map((p) => <AuthImg key={p} path={p} className="h-12 w-12 rounded-md object-cover border border-slate-200" />))}
          </div>
        )}
      </div>

      {signed ? (
        <SignedBanner sig={insp.client_signature} docLabel="l'état des lieux" />
      ) : (
        <>
          {zonesMissing.length > 0 && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="edl-photos-missing">
              <div className="flex items-center gap-2 text-sm font-bold text-amber-800 mb-1">
                <AlertTriangle className="h-4 w-4" /> Photos manquantes : {zonesMissing.join(", ")}
              </div>
              <p className="text-xs text-amber-700 mb-2">Ajoutez au moins 1 photo par zone (étape 2), ou justifiez une exception.</p>
              {!showException ? (
                <button onClick={() => setShowException(true)} className="text-xs font-bold text-amber-800 underline" data-testid="edl-exception-toggle">
                  Justifier une exception
                </button>
              ) : (
                <textarea
                  value={exceptionReason}
                  onChange={(e) => setExceptionReason(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-amber-300 px-3 py-2 text-xs focus:ring-2 focus:ring-amber-500 outline-none"
                  placeholder="Motif de l'exception (ex : pluie battante, parking souterrain sombre…)"
                  data-testid="edl-exception-reason"
                />
              )}
            </div>
          )}
          <PresentialSignBlock
            defaultName={state.booking.client_name}
            mentionDoc="Je reconnais avoir consulté l'état des lieux de départ ci-dessus, j'en accepte le contenu et je consens à le signer électroniquement en présence du loueur, sur son appareil."
            onSign={sign}
            signing={signing}
            blockedReason={zonesMissing.length > 0 && !exceptionReason.trim() ? "Ajoutez au moins 1 photo par zone (étape 2) ou justifiez une exception ci-dessus pour activer la signature." : null}
          />
        </>
      )}
    </StepCard>
  );
}

export function RecapTile({ label, value, warn }) {
  return (
    <div className={`rounded-xl p-3 ${warn ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
      <div className="text-[10px] text-slate-500 font-semibold uppercase">{label}</div>
      <div className={`font-semibold text-sm ${warn ? "text-amber-700" : "text-slate-900"} truncate`}>{value}</div>
    </div>
  );
}

export function SignedBanner({ sig, docLabel }) {
  return (
    <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="signed-banner">
      <div className="flex items-center gap-2 text-sm font-bold text-emerald-700 mb-1">
        <CheckCircle2 className="h-4 w-4" /> Le client a signé {docLabel}
      </div>
      <div className="text-xs text-emerald-800">
        {sig.signer_name} · {new Date(sig.signed_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
        {sig.presential && <span className="block mt-0.5 italic">« {sig.mention} »</span>}
      </div>
      {sig.data_base64 && <img src={sig.data_base64} alt="Signature" className="h-12 mt-2 object-contain" />}
    </div>
  );
}

// ─── Étape 6 : Contrat ───
function StepContract({ bookingId, booking, contract, onSigned }) {
  const [signing, setSigning] = useState(false);
  const [hasRead, setHasRead] = useState(false);

  const sign = async ({ signature_data, signer_name, consent }) => {
    setSigning(true);
    try {
      await api.post(`/contracts/${bookingId}/sign-presential`, { signature_data, signer_name, consent });
      toast.success("Contrat signé");
      onSigned();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur lors de la signature");
    } finally { setSigning(false); }
  };

  if (!contract) {
    return (
      <StepCard title="Contrat de location" instruction="Chargement du contrat…" testid="step-flow-contract">
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
      </StepCard>
    );
  }

  const signed = !!contract.client_signature;
  const legal = contract.legal_snapshot || {};

  return (
    <StepCard title="Contrat de location" instruction={signed ? "Le contrat est déjà signé — étape terminée." : "Le client consulte le contrat puis le signe sur cet appareil."} testid="step-flow-contract">
      {/* Document consultable */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="contract-document">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">Contrat {contract.reference || ""}</div>
            <div className="text-[11px] text-slate-500">{contract.vendor_company} · {booking.client_name}</div>
          </div>
          <a href={`/contract/${bookingId}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-violet-600 hover:underline" data-testid="contract-fullview-link">
            Version complète
          </a>
        </div>
        <div className="max-h-72 overflow-y-auto px-4 py-3 text-xs text-slate-700 space-y-2.5" data-testid="contract-scroll-body">
          <div className="grid grid-cols-2 gap-2">
            <RecapTile label="Véhicule" value={`${contract.vehicle_name}${contract.vehicle_plate ? ` · ${contract.vehicle_plate}` : ""}`} />
            <RecapTile label="Période" value={`${formatDateFR(contract.start_date)} → ${formatDateFR(contract.end_date)}`} />
            <RecapTile label="Total" value={formatEUR(contract.total || 0)} />
            <RecapTile label="Caution" value={contract.caution_amount ? formatEUR(contract.caution_amount) : "—"} />
          </div>
          {legal.franchise_amount != null && <p className="font-semibold">Franchise en cas de dommage : {formatEUR(legal.franchise_amount)}</p>}
          {contract.custom_body ? (
            <p className="whitespace-pre-wrap">{contract.custom_body}</p>
          ) : (
            <ol className="list-decimal pl-4 space-y-1.5">
              {(contract.conditions || []).map((c, i) => <li key={i}>{c}</li>)}
            </ol>
          )}
          {legal.contract_text && <p className="whitespace-pre-wrap">{legal.contract_text}</p>}
          {legal.mentions && <p className="whitespace-pre-wrap text-slate-500">{legal.mentions}</p>}
        </div>
      </div>

      {signed ? (
        <SignedBanner sig={contract.client_signature} docLabel="le contrat" />
      ) : (
        <>
          <label className="mt-4 flex items-start gap-2.5 cursor-pointer" data-testid="contract-read-label">
            <input
              type="checkbox"
              checked={hasRead}
              onChange={(e) => setHasRead(e.target.checked)}
              className="h-4 w-4 mt-0.5 rounded border-slate-300 text-violet-600"
              data-testid="contract-read-checkbox"
            />
            <span className="text-xs text-slate-600 font-medium">Le client confirme avoir lu et compris l'intégralité du contrat.</span>
          </label>
          {hasRead && (
            <PresentialSignBlock
              defaultName={booking.client_name}
              mentionDoc="Je reconnais avoir lu le contrat de location ci-dessus, j'en accepte les conditions et je consens à le signer électroniquement en présence du loueur, sur son appareil."
              onSign={sign}
              signing={signing}
            />
          )}
        </>
      )}
    </StepCard>
  );
}

// ─── Statut d'envoi des documents (partagé départ/retour) ───
export function DocsEmailStatus({ info, phase, bookingId, onResent }) {
  const [sending, setSending] = useState(false);
  if (!info) return null;
  const LABELS = {
    simulated: { txt: "Documents PDF générés — email SIMULÉ (fournisseur email non configuré)", cls: "bg-amber-50 border-amber-200 text-amber-700" },
    sent: { txt: "Documents PDF envoyés au client par email", cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
    failed: { txt: `Échec de l'envoi des documents${info.error ? ` : ${info.error}` : ""}`, cls: "bg-red-50 border-red-200 text-red-700" },
    invalid_recipient: { txt: "Email du client manquant ou invalide — envoi impossible", cls: "bg-red-50 border-red-200 text-red-700" },
    duplicate_skipped: { txt: "Envoi ignoré (doublon récent)", cls: "bg-slate-50 border-slate-200 text-slate-600" },
  };
  const meta = LABELS[info.status] || { txt: `Statut d'envoi : ${info.status}`, cls: "bg-slate-50 border-slate-200 text-slate-600" };
  const canRetry = ["failed", "invalid_recipient", "error"].includes(info.status);
  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/vendor/bookings/${bookingId}/documents/send?phase=${phase}`);
      onResent?.(data.email);
      toast.success("Nouvelle tentative d'envoi effectuée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally { setSending(false); }
  };
  return (
    <div className={`mx-auto max-w-xs mb-4 rounded-xl border px-4 py-3 text-xs font-semibold ${meta.cls}`} data-testid={`docs-email-status-${phase}`}>
      {meta.txt}
      {canRetry && (
        <button onClick={resend} disabled={sending} className="block mx-auto mt-2 underline font-bold" data-testid={`docs-email-retry-${phase}`}>
          {sending ? "Envoi…" : "Réessayer l'envoi"}
        </button>
      )}
    </div>
  );
}

// ─── Étape 7 : Remise des clés ───
function StepKeys({ bookingId, state, onDone, goTo }) {
  const [confirming, setConfirming] = useState(false);
  const [docsEmail, setDocsEmail] = useState(null);
  const { checklist, booking } = state;
  const canConfirm = checklist.edl_signed && checklist.contract_signed && checklist.mileage_done;

  useEffect(() => {
    if (checklist.keys_handed && !docsEmail) {
      api.get(`/vendor/bookings/${bookingId}/documents`)
        .then(({ data }) => setDocsEmail(data.departure_docs_email))
        .catch(() => {});
    }
  }, [checklist.keys_handed]); // eslint-disable-line

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/vendor/bookings/${bookingId}/keys-handover`);
      setDocsEmail(data.docs_email || null);
      toast.success("Clés remises — réservation En cours");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Remise des clés impossible");
    } finally { setConfirming(false); }
  };

  if (checklist.keys_handed) {
    return (
      <div className="text-center py-8" data-testid="keys-success">
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <KeyRound className="h-8 w-8 text-emerald-600" />
        </div>
        <h2 className="font-heading text-2xl font-semibold text-slate-900">Bon voyage ! 🎉</h2>
        <p className="text-sm text-slate-500 mt-2 mb-1">
          Les clés ont été remises à <span className="font-semibold text-slate-900">{booking.client_name}</span>.
        </p>
        <p className="text-xs text-slate-400 mb-6">
          Réservation passée au statut <span className="font-bold text-sky-600">En cours</span>
          {booking.keys_handed_at ? ` · ${new Date(booking.keys_handed_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}
        </p>
        <DocsEmailStatus info={docsEmail} phase="departure" bookingId={bookingId} onResent={setDocsEmail} />
        <div className="flex flex-col gap-2 max-w-xs mx-auto">
          <a href={`/vendor/bookings/${bookingId}`} className="h-11 rounded-xl bg-slate-900 text-white text-sm font-bold flex items-center justify-center" data-testid="keys-open-booking">
            Ouvrir la réservation
          </a>
          <a href="/vendor/pilotage?tab=today" className="h-11 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 flex items-center justify-center" data-testid="keys-back-today">
            Retour aux opérations du jour
          </a>
        </div>
      </div>
    );
  }

  const items = [
    { ok: checklist.info_verified, label: "Informations vérifiées", stepKey: "verify", required: false },
    { ok: checklist.photos_done, label: "Photos du véhicule", stepKey: "photos", required: false },
    { ok: checklist.mileage_done, label: "Kilométrage relevé", stepKey: "readings", required: true },
    { ok: checklist.edl_signed, label: "État des lieux signé par le client", stepKey: "sign_edl", required: true },
    { ok: checklist.contract_signed, label: "Contrat signé par le client", stepKey: "contract", required: true },
  ];

  return (
    <StepCard title="Remise des clés" instruction="Dernière étape : vérifiez que tout est signé, puis confirmez la remise des clés." testid="step-flow-keys">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-1" data-testid="keys-checklist">
        {items.map((it) => (
          <div key={it.label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
            <div className="flex items-center gap-2.5 text-sm">
              {it.ok ? <CheckCircle2 className="h-4.5 w-4.5 text-emerald-500" style={{ height: 18, width: 18 }} /> : <Circle className="h-4.5 w-4.5 text-slate-300" style={{ height: 18, width: 18 }} />}
              <span className={it.ok ? "text-slate-900 font-medium" : "text-slate-500"}>
                {it.label} {it.required && !it.ok && <span className="text-red-500 font-bold">*</span>}
              </span>
            </div>
            {!it.ok && (
              <button onClick={() => goTo(it.stepKey)} className="text-xs font-bold text-violet-600 hover:underline" data-testid={`keys-goto-${it.stepKey}`}>
                Compléter
              </button>
            )}
          </div>
        ))}
      </div>
      {!canConfirm && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs font-semibold text-amber-700" data-testid="keys-blocked-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          La remise des clés nécessite l'état des lieux ET le contrat signés par le client, ainsi que le kilométrage relevé.
        </div>
      )}
      <button
        onClick={confirm}
        disabled={!canConfirm || confirming}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 h-13 py-3.5 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="keys-confirm-btn"
      >
        {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
        Confirmer la remise des clés
      </button>
    </StepCard>
  );
}
